import { apply, burst, celebrate, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import { dayId } from '../core/state';
import { m } from '../i18n';
import { npcName, objName } from '../i18n/labels';
import { sfx, tone } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { say, type Choice } from '../ui/dialog';
import { onSkyFrame } from '../render/weather';
import { drawPhoto } from '../render/propsXinyi';
import { MAPS } from '../world/map';
import { px, type ObjectId } from '../world/mapdef';
import type { World } from '../world/world';
import { mrtMenu, order, quiz } from './trips';
import { stamp } from './passport';
import { registerObjects, registerTalk } from './registry';

/** Interactions of the Taipei map (taipei.ts) and the MRT station (xinyi package). */
const X = () => m().xinyi;
const pick = <T>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)];
const H = (h: number, mm = 0) => h * 60 + mm;

let world: World | null = null;

/** A fact, then an English quiz; the reward is claimed once per day. */
function factQuiz(id: ObjectId, icon: string, text: string, q: string, a: readonly string[], ok: string, reward: { stars?: number; study?: number; mood?: number } = { stars: 2, study: 1 }, extra: Choice[] = []): void {
  const c = X().common;
  say({
    who: objName(id),
    icon,
    text,
    choices: [
      { text: c.quiz, run: () => quiz(objName(id), icon, text, q, a, ok, c.wrong, () => onceToday(`xy.${id}`) && apply(reward)) },
      ...extra,
      { text: c.back },
    ],
  });
}

/** Say an English line out loud with the replay / shadowing row. */
function line(who: string, icon: string, text: string, en?: string): void {
  if (en) sayEnglish(en, 0.9);
  say({ who, icon, text, en });
}

/** “English practice: …” lines carry their quote in the replay row. */
function talkLine(who: string, icon: string, text: string): void {
  // The label is translated in other locales, but the quoted English phrase is not.
  const q = /“([^”]+)”/.exec(text);
  line(who, icon, text, q && /^[\x20-\x7e’]+$/.test(q[1]) ? q[1] : undefined);
}

function politeAsk(id: ObjectId, icon: string, howAsk: string, ok: string, bad: readonly string[], done: string, onOk: () => void): void {
  const opts = [ok, ...bad].map((o) => [Math.random(), o] as const).sort((p, q) => p[0] - q[0]).map((p) => p[1]);
  say({
    who: objName(id),
    icon,
    text: howAsk,
    choices: opts.map((o) => ({
      text: `🗣️ ${o}`,
      run: () => {
        sayEnglish(o, 0.9);
        if (o !== ok) {
          sfx('bad');
          return say({ who: objName(id), icon, text: X().common.wrong, choices: [{ text: m().common.back, run: () => politeAsk(id, icon, howAsk, ok, bad, done, onOk) }] });
        }
        sfx('ok');
        onOk();
        say({ who: objName(id), icon, text: done });
      },
    })),
  });
}

// --- Bubble tea ---------------------------------------------------------------------------------

function bubbleTea(): void {
  const t = X().tea;
  say({
    who: objName('xy_tea'),
    icon: '🧋',
    text: `${t.text}\n\n${t.menu}`,
    choices: [
      { text: t.milk, run: () => order('xy_tea', '🧋', t.milkOrder, { mood: 12, energy: 6 }) },
      { text: t.green, run: () => order('xy_tea', '🍵', t.greenOrder, { mood: 8, energy: 4 }) },
      { text: X().common.back },
    ],
  });
}

// --- Taxi -----------------------------------------------------------------------------------------

const TAXI_PRICE = 30;
const TAXI_TO = {
  station: { x: px(16), y: px(15) + 8, facing: 'up' as const },
  tower: { x: px(75), y: px(15) + 8, facing: 'up' as const },
  village: { x: px(44) + 8, y: px(35) + 8, facing: 'up' as const },
  trail: { x: px(82) + 8, y: px(50) + 8, facing: 'right' as const },
};

function taxi(): void {
  const t = X().taxi;
  sayEnglish('Where to?', 0.9);
  say({
    who: npcName('xy_taxiDriver'),
    icon: '🚕',
    text: t.text,
    en: 'Where to?',
    choices: [
      ...(Object.keys(TAXI_TO) as (keyof typeof TAXI_TO)[]).map(
        (k): Choice => ({
          text: `🗣️ ${t.to[k]}`,
          cost: t.price(TAXI_PRICE),
          run: () => {
            sayEnglish(t.to[k], 0.9);
            if (S().coins < TAXI_PRICE) return toast(t.poor);
            apply({ coins: -TAXI_PRICE, minutes: 15 }, { float: false });
            sfx('door');
            world?.travel('taipei', TAXI_TO[k]);
            window.setTimeout(() => toast(t.ride), 600);
          },
        }),
      ),
      { text: X().common.back },
    ],
  });
}

// --- Photo at the viewpoint ---------------------------------------------------------------------

function showPhoto(): void {
  const t = X().viewpoint;
  const night = S().time >= H(18, 30) || S().time < H(5, 30);
  const dusk = !night && S().time >= H(17);
  const wrap = document.createElement('div');
  wrap.setAttribute('role', 'dialog');
  wrap.setAttribute('aria-label', t.title);
  wrap.style.cssText = 'position:fixed;inset:0;z-index:60;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;background:rgba(10,8,14,0.82);';
  const frame = document.createElement('div');
  frame.style.cssText = 'background:#fbf8f0;padding:10px 10px 50px;border-radius:4px;box-shadow:0 10px 40px rgba(0,0,0,0.5);transform:rotate(-2deg);max-width:86vw;position:relative;';
  const cv = document.createElement('canvas');
  const W = 300, Hh = 220, k = Math.min(3, window.devicePixelRatio || 1);
  cv.width = W * k;
  cv.height = Hh * k;
  cv.style.cssText = `width:min(${W}px,80vw);height:auto;display:block;image-rendering:pixelated;`;
  const c = cv.getContext('2d')!;
  c.scale(k, k);
  drawPhoto(c, W, Hh, night ? 'night' : dusk ? 'dusk' : 'day');
  const cap = document.createElement('div');
  cap.textContent = t.caption;
  cap.style.cssText = 'position:absolute;left:0;right:0;bottom:8px;text-align:center;padding:0 10px;font:600 12px system-ui,sans-serif;color:#3a2a20;';
  const hint = document.createElement('div');
  hint.textContent = t.tap;
  hint.style.cssText = 'font:600 12px system-ui,sans-serif;color:#fff3d0;opacity:0.8;';
  frame.append(cv, cap);
  wrap.append(frame, hint);
  const close = () => {
    wrap.remove();
    window.removeEventListener('keydown', onKey, true);
    line(objName('xy_viewpoint'), '📸', `“${t.say}”`, t.say);
  };
  const onKey = (e: KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    close();
  };
  wrap.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    e.preventDefault();
    close();
  });
  window.addEventListener('keydown', onKey, true);
  document.body.appendChild(wrap);
}

function viewpoint(): void {
  const t = X().viewpoint;
  say({
    who: objName('xy_viewpoint'),
    icon: '📸',
    text: t.text,
    choices: [
      {
        text: t.shoot,
        run: () => {
          sfx('pop');
          celebrate();
          if (onceToday('xy.viewpoint')) apply({ mood: 15, stars: 4 });
          stamp('xiangshan');
          S().forever['xy.photo101'] = true;
          showPhoto();
        },
      },
      { text: X().common.back },
    ],
  });
}

// --- Simple Market (Sundays) ----------------------------------------------------------------------

function marketStall(id: 'xy_market1' | 'xy_market2' | 'xy_market3'): void {
  const t = X().market;
  const st = t.stalls[id];
  sayEnglish(t.ask, 0.9);
  say({
    who: objName(id),
    icon: '🧺',
    text: `${st.text}\n\n🗣️ “${t.ask}”\n“${st.answer}”`,
    en: st.answer,
    choices: [
      {
        text: t.buy(st.price),
        run: () => {
          if (S().coins < st.price) return toast(t.poor);
          apply({ coins: -st.price, mood: 10 });
          if (onceToday(`xy.${id}`)) apply({ stars: 2 }, { float: false });
          sfx('ok');
          burst('sparkle', 6);
          say({ who: objName(id), icon: '🎁', text: t.bought });
        },
      },
      { text: X().common.back },
    ],
  });
}

// --- EasyCard ---------------------------------------------------------------------------------------

function easyCard(): void {
  const t = X().easycard;
  const c = X().common;
  say({
    who: objName('xy_easycard'),
    icon: '💳',
    text: t.text,
    choices: [
      {
        text: t.top,
        run: () =>
          quiz(objName('xy_easycard'), '💳', t.text, t.q, t.a, c.right, c.wrong, () =>
            window.setTimeout(
              () =>
                quiz(objName('xy_easycard'), '💳', t.text, t.q2, t.a2, t.ok, c.wrong, () => {
                  sfx('checkout');
                  if (onceToday('xy.easycard')) apply({ stars: 3, study: 2 });
                }),
              900,
            ),
          ),
      },
      { text: c.back },
    ],
  });
}

// --- Objects ----------------------------------------------------------------------------------------

const performing = () => {
  const tm = S().time;
  return tm >= H(10) && tm < H(22);
};
const truckHere = () => S().time >= H(19, 50) && S().time < H(20, 30);

registerObjects({
  xy_sysHall() {
    const t = X().sysHall;
    factQuiz('xy_sysHall', '🏛️', t.text, t.q, t.a, t.ok, { stars: 2, study: 1 }, [
      { text: t.watch, run: () => (sfx('fanfare'), onceToday('xy.guards') && apply({ mood: 8 }), say({ who: objName('xy_sysHall'), icon: '💂', text: t.watched })) },
    ]);
  },
  xy_cuihu() {
    const t = X().cuihu;
    say({
      who: objName('xy_cuihu'),
      icon: '🐟',
      text: t.text,
      choices: [{ text: t.look, run: () => (sfx('water'), onceToday('xy.cuihu') && apply({ mood: 8 }), line(objName('xy_cuihu'), '🐟', `“${t.say}”\n\n${t.looked}`, t.say)) }, { text: X().common.back }],
    });
  },
  xy_mrtSys: () => (sfx('door'), mrtMenu()),
  xy_mrt101: () => (sfx('door'), mrtMenu()),
  xy_mall101() {
    const t = X().mall;
    factQuiz('xy_mall101', '🛍️', t.text, t.q, t.a, t.ok, { stars: 2, study: 1 }, [
      { text: t.browse, run: () => (onceToday('xy.browse') && apply({ mood: 6, minutes: 20 }), say({ who: objName('xy_mall101'), icon: '🛍️', text: t.browsed })) },
    ]);
  },
  xy_love() {
    const t = X().love;
    say({
      who: objName('xy_love'),
      icon: '❤️',
      text: t.text,
      choices: [
        { text: t.ask, run: () => politeAsk('xy_love', '📸', t.howAsk, t.ok, t.bad, t.done, () => (burst('heart', 8), onceToday('xy.love') && apply({ mood: 10, stars: 2 }))) },
        { text: X().common.back },
      ],
    });
  },
  xy_fireworks() {
    const t = X().fireworks;
    sayEnglish(t.countdown, 0.95);
    factQuiz('xy_fireworks', '🎆', `${t.text}\n\n“${t.countdown}”`, t.q, t.a, t.ok);
  },
  xy_conv() {
    const t = X().conv;
    sfx('ring');
    say({
      who: objName('xy_conv'),
      icon: '🏪',
      text: t.text,
      choices: [
        { text: t.snack, run: () => order('xy_conv', '🥚', t.food, { energy: 14, mood: 4 }) },
        { text: t.bag, run: () => quiz(objName('xy_conv'), '🛍️', t.text, t.bagQ, t.bagA, t.bagOk, X().common.wrong, () => onceToday('xy.bag') && apply({ stars: 2, study: 1 })) },
        { text: X().common.back },
      ],
    });
  },
  xy_tea: bubbleTea,
  xy_pharmacy() {
    const t = X().pharmacy;
    factQuiz('xy_pharmacy', '💊', t.text, t.q, t.a, t.ok, { stars: 1, study: 2 });
  },
  xy_trash() {
    const t = X().trash;
    factQuiz('xy_trash', '🗑️', `${t.text}\n\n${truckHere() ? t.here : t.notYet}`, t.q, t.a, t.ok);
  },
  xy_shrine() {
    const t = X().shrine;
    say({
      who: objName('xy_shrine'),
      icon: '🙏',
      text: t.text,
      choices: [
        { text: t.bow, run: () => (sfx('bell'), onceToday('xy.shrine') && apply({ mood: 8 }), line(objName('xy_shrine'), '🙏', `${t.bowed}\n\n“${t.wish}”`, t.wish)) },
        { text: X().common.back },
      ],
    });
  },
  xy_village() {
    const t = X().village;
    factQuiz('xy_village', '🏘️', `${t.text}\n\n${dayId(S()) === 'sun' ? t.market : t.noMarket}`, t.q, t.a, t.ok);
  },
  xy_bagel: () => order('xy_bagel', '🥯', X().bagel, { energy: 22, mood: 8 }),
  xy_market1: () => marketStall('xy_market1'),
  xy_market2: () => marketStall('xy_market2'),
  xy_market3: () => marketStall('xy_market3'),
  xy_dept() {
    const t = X().dept;
    factQuiz('xy_dept', '🛍️', t.text, t.q, t.a, t.ok, { stars: 2, mood: 4 });
  },
  xy_books() {
    const t = X().books;
    const tm = S().time;
    if (tm < H(10)) return say({ who: objName('xy_books'), icon: '📚', text: t.closed });
    say({
      who: objName('xy_books'),
      icon: '📚',
      text: t.text,
      choices: [
        { text: t.read, cost: m().common.minutes(30), run: () => (apply({ minutes: 30, mood: 4, study: onceToday('xy.read') ? 6 : 2 }), sfx('rustle'), say({ who: objName('xy_books'), icon: '📖', text: t.readDone })) },
        { text: t.askClerk, run: () => (sayEnglish(t.clerkSays.replace(/^[^“]*“|”$/g, ''), 0.9), quiz(npcName('xy_clerk'), '📚', t.clerkSays, t.q, t.a, t.ok, X().common.wrong, () => onceToday('xy.books') && apply({ stars: 2, study: 2 }))) },
        { text: X().common.back },
      ],
    });
  },
  xy_screen() {
    const t = X().screen;
    factQuiz('xy_screen', '📺', t.text, t.q, t.a, t.ok);
  },
  xy_stage() {
    const t = X().stage;
    if (!performing()) return say({ who: objName('xy_stage'), icon: '🎭', text: `${t.text}\n\n${t.empty}` });
    say({
      who: objName('xy_stage'),
      icon: '🎭',
      text: t.text,
      choices: [
        { text: t.watch, cost: m().common.minutes(15), run: () => (sfx('fanfare'), burst('note', 8), apply({ minutes: 15, mood: onceToday('xy.show') ? 12 : 3 }), say({ who: objName('xy_stage'), icon: '🎶', text: t.watched })) },
        {
          text: t.tip,
          run: () => {
            if (S().coins < 5) return toast(X().market.poor);
            apply({ coins: -5, mood: 4 });
            burst('heart', 5);
            line(objName('xy_stage'), '🪙', `“${t.thanks}”`, t.thanks);
          },
        },
        { text: X().common.back },
      ],
    });
  },
  xy_trailhead() {
    const t = X().trail;
    factQuiz('xy_trailhead', '🥾', t.text, t.q, t.a, t.ok, { stars: 1, study: 1 });
  },
  xy_rocks() {
    const t = X().rocks;
    say({
      who: objName('xy_rocks'),
      icon: '🪨',
      text: t.text,
      choices: [
        {
          text: t.climb,
          run: () => {
            sfx('hop');
            apply({ energy: -4, minutes: 10 }, { float: false });
            if (onceToday('xy.rocks')) apply({ mood: 10, stars: 2 });
            line(objName('xy_rocks'), '🧗', `“${t.careful}”\n\n${t.climbed}`, t.careful);
          },
        },
        { text: X().common.back },
      ],
    });
  },
  xy_viewpoint: viewpoint,
  xy_taxi: taxi,
  xy_easycard: easyCard,
  xy_priority() {
    const t = X().priority;
    factQuiz('xy_priority', '💺', t.text, t.q, t.a, t.ok, { stars: 2, study: 1 });
  },
});

// --- People -----------------------------------------------------------------------------------------

function tourists(): void {
  const t = X().talk;
  sayEnglish(t.tourAsk, 0.9);
  quiz(npcName('xy_tourDad'), '🧳', `“${t.tourAsk}”`, t.tourQ, t.tourA, t.tourOk, X().common.wrong, () => {
    if (onceToday('xy.tourists')) apply({ stars: 3, study: 2, mood: 6 });
  });
}

registerTalk((id) => {
  const t = X().talk;
  switch (id) {
    case 'xy_office1':
      talkLine(npcName(id), '💼', pick(t.office1));
      return true;
    case 'xy_office2':
      talkLine(npcName(id), '💼', pick(t.office2));
      return true;
    case 'xy_tourDad':
      tourists();
      return true;
    case 'xy_tourKid':
      talkLine(npcName(id), '🎈', pick(t.tourKid));
      return true;
    case 'xy_grandma':
      talkLine(npcName(id), '👵', pick(S().time < H(11) || S().time >= H(17) ? t.grandmaShrine : t.grandmaMarket));
      return true;
    case 'xy_hiker':
      talkLine(npcName(id), '🥾', pick(t.hiker));
      return true;
    case 'xy_clerk':
      talkLine(npcName(id), '📚', pick(t.clerk));
      return true;
    case 'xy_teaSeller':
      sayEnglish(t.teaSeller, 0.9);
      bubbleTea();
      return true;
    case 'xy_trashLady':
      talkLine(npcName(id), '🗑️', pick(t.trashLady));
      return true;
    case 'xy_vendor':
      talkLine(npcName(id), '🧺', pick(t.vendor));
      return true;
    case 'xy_taichi':
      talkLine(npcName(id), '🧘', pick(t.taichi));
      return true;
    case 'xy_taxiDriver':
      taxi();
      return true;
    case 'xy_performer':
      burst('heart', 4);
      talkLine(npcName(id), '🎈', pick(t.performer));
      return true;
    default:
      return false;
  }
});

// --- Per-frame: the garbage truck's music and a first-visit welcome ------------------------------------

let lastMap = '';
onSkyFrame((w) => {
  world = w;
  const id = w.map.id;
  if (id !== 'taipei') {
    lastMap = id;
    return;
  }
  if (lastMap !== 'taipei' && lastMap !== '' && onceToday('xy.welcome')) window.setTimeout(() => toast(X().welcome), 1500);
  lastMap = id;
  if (truckHere() && onceToday('xy.truck')) toast(X().trash.toast);
  if (truckHere()) truckMusic(w);
});

// The truck's loudspeaker plays "A Maiden's Prayer" (少女的祈禱, Bądarzewska 1856, public domain),
// the main theme as in the IMSLP incipit, an octave lower; louder as you get closer.
const TRUCK_AT = { x: px(22), y: px(39) };
const hz = (midi: number) => 440 * 2 ** ((midi - 69) / 12);
const [Bb2, Eb2, Ab2, D3, F3, Bb3, D4, Eb4, F4, G4, Ab4, Bb4, C5, D5, Eb5, F5, G5] = [46, 39, 44, 50, 53, 58, 62, 63, 65, 67, 68, 70, 72, 74, 75, 77, 79].map(hz);
const t3 = 1 / 3;
// [frequency (0 = rest), beats] — Andante, 4/4.
const PRAYER: [number, number][] = [
  [0, t3], [Bb3, t3], [Eb4, t3], [G4, t3], [Bb4, t3], [Eb5, t3], [G5, 1.5], [F5, 0.5],
  [F5, 0.75], [Eb5, 0.25], [Eb5, t3], [D5, t3], [C5, t3], [C5, 1], [0, 1],
  [0, t3], [D3, t3], [F3, t3], [Bb3, t3], [D4, t3], [F4, t3], [D5, 1.5], [C5, 0.5],
  [C5, 0.75], [Bb4, 0.25], [Bb4, t3], [Ab4, t3], [G4, t3], [G4, 1], [0, 1],
];
/** One bass note per bar: E♭, A♭, B♭, E♭. */
const BASS = [Eb2, Ab2, Bb2, Eb2];
const BEAT = 0.62;
const PHRASE = PRAYER.reduce((a, [, d]) => a + d, 0) * BEAT;
let nextTune = 0;
function truckMusic(w: World): void {
  const now = performance.now() / 1000;
  if (now < nextTune) return;
  const d = Math.hypot(w.player.x - TRUCK_AT.x, w.player.y - TRUCK_AT.y) / 16;
  const vol = d < 4 ? 0.05 : Math.max(0, 0.05 * (1 - (d - 4) / 36));
  nextTune = now + PHRASE + 2.5;
  if (vol < 0.004) return;
  let at = 0;
  // A music-box loudspeaker: the melody with a soft bass note on each bar.
  for (const [f, beats] of PRAYER) {
    if (f) tone(f, Math.max(beats, 0.5) * BEAT * 1.4, 'triangle', vol, at);
    at += beats * BEAT;
  }
  BASS.forEach((f, i) => tone(f, 4 * BEAT * 0.9, 'sine', vol * 0.6, i * 4 * BEAT));
}

/** Spots other code can use (taxi destinations double as handy teleports in tests). */
export const XINYI_SPOTS = { ...TAXI_TO, train: MAPS.taipei.spots!.train, mrt: MAPS.taipei.spots!.mrt };
